import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { primaryEndpoint, errNetworkCodes } from 'src/common/config/TextMessage';

// Centralized retry rule set shared by queries and mutations
const defaultRetryLogic = (failureCount: number, error: any) => {
  if (failureCount >= 3) {
    return false;
  }

  const status = error?.status || error?.response?.status;
  const code = error?.code || error?.response?.code || error?.message;

  const retryableStatusCodes = [500, 502, 503, 504, 408];

  const isRetryableStatus = retryableStatusCodes.includes(status);
  const isNetworkError =
    errNetworkCodes.includes(code) ||
    (typeof code === 'string' && (
      code.includes('ERR_NETWORK') ||
      code.includes('blocked')
    ));

  return isRetryableStatus || isNetworkError;
};

/**
 * Single, production-configured QueryClient instance.
 * Houses state of queries and mutations for the entire application.
 */
export const queryClient = new QueryClient({
  // Global caches for queries and mutations to attach central error interceptors
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      // Avoid global error alerts/notifications if a query has explicitly disabled it in meta
      if (query.meta?.preventGlobalError === true) {
        return;
      }

      // Existing recovery logic: check for 502 or network error on primary endpoints to reload the page
      const status = error?.status || error?.response?.status;
      const code = error?.code || error?.response?.code || error?.message;
      const configUrl = error?.config?.url || '';

      const isNetworkError =
        errNetworkCodes.includes(code) ||
        (typeof code === 'string' && (
          code.includes('ERR_NETWORK') ||
          code.includes('blocked')
        ));

      const isPrimaryEndpoint =
        configUrl &&
        primaryEndpoint.some(
          (endpoint: string) => endpoint && configUrl.includes(endpoint)
        );

      if ((status === 502 || isNetworkError) && isPrimaryEndpoint) {
        // If not already on the status check page, trigger auto-recovery and reload
        if (window.location.pathname !== '/status-check' && window.location.pathname !== '/statusCheck') {
          try {
            localStorage.setItem('api_down', 'true');
            window.location.reload();
          } catch (reloadErr) {
            console.error('Failed to trigger API down page reload', reloadErr);
          }
        }
      }

      const queryKeyString = Array.isArray(query.queryKey) ? query.queryKey.join('.') : String(query.queryKey);
      // handleGlobalError(error, 'query', `QueryKey: [${queryKeyString}]`);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Avoid global error alerts/notifications if a mutation has explicitly disabled it in meta
      if (mutation.meta?.preventGlobalError === true) {
        return;
      }
      const mutationKey = mutation.options.mutationKey;
      const mutationKeyString = mutationKey
        ? (Array.isArray(mutationKey) ? mutationKey.join('.') : String(mutationKey))
        : 'Unnamed';
      // handleGlobalError(error, 'mutation', `MutationKey: [${mutationKeyString}]`);
    },
  }),

  // Production-grade default configurations
  defaultOptions: {
    queries: {
      // Treat data as fresh for 5 minutes before considering it stale.
      // This prevents redundant, identical requests when components mount/remount.
      staleTime: 5 * 60 * 1000,

      // Keep unused query data in memory for 10 minutes before garbage collecting it.
      gcTime: 10 * 60 * 1000,

      // In production, shift-tabbing or switching windows should not trigger an automatic refetch
      // of all active queries unless explicitly needed on specific queries.
      refetchOnWindowFocus: false,

      // Automatically refetch queries when the internet connection is restored.
      refetchOnReconnect: true,

      // Retry failed queries up to 3 times with exponential backoff (like legacy API retries).
      // Restricts retries strictly to network errors and specific server status codes.
      retry: false,
    },
    mutations: {
      // Allow mutations (like Login) to retry under the exact same retryable conditions
      retry: false,
    },
  },
});

// TypeScript only:
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__:
      import('@tanstack/query-core')
        .QueryClient
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient